import { Spinner } from '@creditornot/cb-progress';
import React, { useMemo } from 'react';
import { Switch, useRouteMatch } from 'react-router-dom';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { RouteWhenAuthenticated } from 'common/components';
import { FeatureType } from 'common/types';
import { useExperienceModuleContext } from 'modules/experience-core';
import { ExperienceEntityType } from 'modules/experience-core';
import { useSelectedEntityVenues } from 'modules/experience-core';

import { CampaignModuleContextProps, CampaignModuleContextProvider } from './context';
import useCampaignTypesPermittedToEdit from './hooks/useCampaignTypesPermittedToEdit';
import { DetailsCampaignPage } from './pages/DetailsCampaignPage';
import { EditCampaignPage } from './pages/EditCampaignPage';
import { ListCampaignsPage } from './pages/ListCampaignsPage/ListCampaignsPage';
import { NewCampaignPage } from './pages/NewCampaignPage/NewCampaignPage';

export const ExperienceCampaigns = () => {
  const { path } = useRouteMatch();
  const {
    timezone,
    currency,
    country,
    selectedExperienceEntity: { experienceEntityType, experienceEntityId, experienceEntityObject },
  } = useExperienceModuleContext();
  const venue = experienceEntityObject as Venue;
  const experienceEntityVenues = useSelectedEntityVenues();
  const venues = experienceEntityVenues.map(venue => venue.experienceEntityObject as Venue);
  const merchantId = experienceEntityType === ExperienceEntityType.MERCHANT ? experienceEntityId : venue.merchant;
  const editPermissionOptions = useMemo(
    () =>
      experienceEntityType === ExperienceEntityType.MERCHANT
        ? {
            feature: experienceEntityType as FeatureType,
            featureId: experienceEntityId,
            entityCountry: country,
          }
        : {
            feature: 'venue' as FeatureType,
            featureId: venue.id,
            city: venue.city,
            entityCountry: venue.country,
            productLine: venue.product_line,
          },
    [experienceEntityType, experienceEntityId, country, venue],
  );
  const { campaignTypesPermittedToEdit, isLoading } = useCampaignTypesPermittedToEdit(editPermissionOptions);

  const props = useMemo(
    () => ({
      merchantId,
      timezone,
      country,
      productLines:
        experienceEntityType === ExperienceEntityType.MERCHANT
          ? [...new Set(venues.map(venue => venue.product_line).flat())]
          : [venue.product_line],
      venueIds: venues.map(venue => venue.id),
      campaignTypesPermittedToEdit,
      menuId: experienceEntityType === ExperienceEntityType.MERCHANT ? venue.menu || undefined : undefined,
      merchantVenues: experienceEntityType === ExperienceEntityType.MERCHANT ? venues : undefined,
    }),
    [merchantId, timezone, country, venues, venue, experienceEntityType, campaignTypesPermittedToEdit],
  );

  const contextValue: CampaignModuleContextProps = {
    currency,
    entityType: experienceEntityType === ExperienceEntityType.MERCHANT ? 'merchant' : 'venue',
    country,
    venues,
    merchantId,
    timezone,
    isExperienceFlow: true,
  };

  if (isLoading) {
    return <Spinner size="large" />;
  }

  return (
    <CampaignModuleContextProvider value={contextValue}>
      <Switch>
        <RouteWhenAuthenticated exact path={path}>
          <ListCampaignsPage {...props} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/new`}>
          <NewCampaignPage {...props} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/:campaignId`}>
          <DetailsCampaignPage {...props} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/:campaignId/edit`}>
          <EditCampaignPage {...props} />
        </RouteWhenAuthenticated>
      </Switch>
    </CampaignModuleContextProvider>
  );
};
